import React, { useEffect, useMemo, useState } from "react";
import { Content } from "antd/es/layout/layout";
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Radio,
  Space,
  Spin,
} from "antd";
import "./style.css";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchAuthSession } from "@aws-amplify/auth";
import { generateClient } from "@aws-amplify/api";
import axios from "axios";
import {
  BankOutlined,
  CheckCircleFilled,
  DollarCircleOutlined,
  ExclamationCircleFilled,
  LeftOutlined,
} from "@ant-design/icons";
import { getTableID } from "../Services/authServices";
import { getUser } from "../graphql/queries";
import canadaFlag from "../Image/ca-circle.png";
import barbadosFlag from "../Image/flag/barbados.png";
import InteracLogo from "../Image/Interac-Logo.wine.svg";
import AppActionModal from "../Components/AppActionModal";
import { getVerificationUiState } from "../Utils/verificationStatus";

const client = generateClient();

const FundWallet = () => {
  const location = useLocation();
  const walletDetails = location.state?.wallet;
  const [form] = Form.useForm();
  const watchedAmount = Form.useWatch("amount", form);
  const [loading, setLoading] = useState(false);
  const [requestResult, setRequestResult] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState({
    identificationVerified: false,
    verificationStatus: "",
    isOrganization: false,
  });
  const navigate = useNavigate();

  const API_URL =
    "https://wkdaviw3pa.execute-api.us-east-2.amazonaws.com/default/AddFundToWallet-staging";

  const paymentMethodOptions = useMemo(
    () => [
      {
        label: "EFT",
        value: "EFT",
        icon: <BankOutlined />,
      },
      {
        label: "Cash",
        value: "Cash",
        icon: <DollarCircleOutlined />,
      },
      {
        label: "Interac",
        value: "Interac",
        icon: (
          <img
            src={InteracLogo}
            alt="Interac"
            style={{ height: 60, width: 60 }}
          />
        ),
      },
    ],
    [],
  );

  const walletID = walletDetails?.id || walletDetails?.walletID || "";
  const walletCurrency = String(walletDetails?.currency || "CAD").toUpperCase();
  const availableBalance = Number(walletDetails?.availableBalance || 0);
  const walletFlag = walletCurrency === "BBD" ? barbadosFlag : canadaFlag;
  const verificationUi = useMemo(
    () =>
      getVerificationUiState({
        verificationStatus: verificationStatus.verificationStatus,
        identificationVerified: verificationStatus.identificationVerified,
        isOrganization: verificationStatus.isOrganization,
      }),
    [verificationStatus],
  );

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {
        const tableID = await getTableID();
        if (!tableID) {
          return;
        }

        const { data } = await client.graphql({
          query: getUser,
          variables: { id: tableID },
        });
        const status = data?.getUser;
        if (!status) {
          return;
        }

        setVerificationStatus({
          identificationVerified: !!status.identificationVerified,
          verificationStatus: status.verificationStatus || "",
          isOrganization: !!status.isOrganization,
        });
      } catch (error) {
        console.error("Error loading verification status:", error);
      }
    };

    fetchVerificationStatus();
  }, []);

  const getFriendlyErrorMessage = (status, fallbackMessage) => {
    if (status === 400) {
      return "Invalid input. Verify payment method and amount format.";
    }
    if (status === 401) {
      return "Unauthorized request. Please sign in again.";
    }
    if (status === 403) {
      return "You are not eligible to add funds at this time. Please complete KYC or contact support.";
    }
    if (status === 500) {
      return "Server error while recording the funding request. Please try again.";
    }
    return fallbackMessage || "Unable to submit fund request right now.";
  };

  const safeParseJson = (value) => {
    if (typeof value !== "string") {
      return value;
    }
    try {
      return JSON.parse(value);
    } catch (parseError) {
      return value;
    }
  };

  const normalizeLambdaResult = (httpStatus, rawData) => {
    const isObjectData = rawData && typeof rawData === "object";
    const hasWrappedStatusCode =
      isObjectData &&
      Object.prototype.hasOwnProperty.call(rawData, "statusCode");

    if (!hasWrappedStatusCode) {
      return {
        ok: httpStatus >= 200 && httpStatus < 300,
        status: httpStatus,
        data: isObjectData
          ? rawData
          : { message: rawData ? String(rawData) : undefined },
      };
    }

    const wrappedStatus = Number(rawData.statusCode);
    const parsedBody = safeParseJson(rawData.body);
    const bodyData =
      parsedBody && typeof parsedBody === "object"
        ? parsedBody
        : { message: typeof parsedBody === "string" ? parsedBody : undefined };

    const mergedData = { ...rawData, ...bodyData };
    if (!mergedData.message && mergedData.error) {
      mergedData.message = mergedData.error;
    }

    const resolvedStatus = Number.isFinite(wrappedStatus)
      ? wrappedStatus
      : httpStatus;

    return {
      ok: resolvedStatus >= 200 && resolvedStatus < 300,
      status: resolvedStatus,
      data: mergedData,
    };
  };

  const MAX_WHOLE_DIGITS = 9;

  const sanitizeAmountInput = (value) => {
    if (!value) return "";
    let cleaned = String(value).replace(/,/g, "").replace(/[^\d.]/g, "");
    const firstDotIndex = cleaned.indexOf(".");
    if (firstDotIndex !== -1) {
      cleaned =
        cleaned.slice(0, firstDotIndex + 1) +
        cleaned.slice(firstDotIndex + 1).replace(/\./g, "");
    }

    const [wholePart = "", decimalPart = ""] = cleaned.split(".");
    const whole = wholePart.slice(0, MAX_WHOLE_DIGITS);
    const decimal = decimalPart.slice(0, 2);
    return cleaned.includes(".") ? `${whole}.${decimal}` : whole;
  };

  const formatAmountInput = (value, info) => {
    if (value === null || value === undefined || value === "") return "";
    const sanitized = sanitizeAmountInput(value);
    if (!sanitized) return "";

    if (info?.userTyping) {
      const [whole, decimal] = sanitized.split(".");
      const wholeWithCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return decimal !== undefined ? `${wholeWithCommas}.${decimal}` : wholeWithCommas;
    }

    const numeric = Number(sanitized);
    if (Number.isNaN(numeric)) return "";
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatCurrencyAmount = (value) => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) {
      return "0.00";
    }
    return numeric.toLocaleString("en-CA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleAmountKeyDown = (event) => {
    if (["e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
      return;
    }

    const { key, target } = event;
    const inputValue = String(target?.value || "");
    const raw = inputValue.replace(/,/g, "");
    const dotIndex = raw.indexOf(".");
    const [whole = "", decimal = ""] = raw.split(".");

    if (key === ".") {
      if (dotIndex !== -1) event.preventDefault();
      return;
    }

    if (!/^\d$/.test(key)) return;

    const selectionStart = target?.selectionStart ?? inputValue.length;
    const selectionEnd = target?.selectionEnd ?? inputValue.length;
    const hasSelection = selectionStart !== selectionEnd;
    if (hasSelection) return;

    const rawCursorPos = inputValue.slice(0, selectionStart).replace(/,/g, "").length;
    const isEditingDecimal = dotIndex !== -1 && rawCursorPos > dotIndex;

    if (!isEditingDecimal && whole.length >= MAX_WHOLE_DIGITS) {
      event.preventDefault();
      return;
    }

    if (isEditingDecimal && decimal.length >= 2) {
      event.preventDefault();
    }
  };

  const goToPreviousPage = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/dashboard");
  };

  const handleSubmit = async (values) => {
    if (isSubmitLocked || loading) {
      return;
    }

    if (!verificationUi.isVerified) {
      setShowVerificationModal(true);
      return;
    }

    if (!walletID) {
      setRequestResult({
        ok: false,
        status: null,
        data: {
          message:
            "Missing wallet ID. Go back and open this page from Dashboard.",
        },
      });
      return;
    }

    const amountValue = sanitizeAmountInput(values.amount).replace(/\.$/, "");
    if (!/^\d+(\.\d{1,2})?$/.test(amountValue) || Number(amountValue) <= 0) {
      setRequestResult({
        ok: false,
        status: 400,
        data: {
          message: "Amount must be greater than 0 with up to 2 decimals.",
        },
      });
      return;
    }

    setLoading(true);
    setRequestResult(null);

    try {
      const session = await fetchAuthSession();
      const jwtToken =
        session?.tokens?.accessToken?.toString() ||
        session?.tokens?.idToken?.toString();

      if (!jwtToken) {
        throw new Error("Missing Cognito JWT token.");
      }

      const payload = {
        walletID,
        paymentMethod: values.paymentMethod,
        Amount: amountValue,
        // Compatibility fallback for non-proxy API Gateway mappings that drop headers.
        authorization: `Bearer ${jwtToken}`,
        token: jwtToken,
      };

      const lambdaResponse = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const normalizedResult = normalizeLambdaResult(
        lambdaResponse.status,
        lambdaResponse.data,
      );
      const normalizedMessage =
        normalizedResult.data?.message || normalizedResult.data?.error;

      if (normalizedResult.ok) {
        setRequestResult(null);
        setIsSubmitLocked(true);
        setSuccessMessage(
          normalizedMessage || "Fund addition request recorded successfully.",
        );
        setShowSuccessModal(true);
      } else {
        setRequestResult({
          ok: false,
          status: normalizedResult.status,
          data: {
            ...(normalizedResult.data || {}),
            message:
              normalizedMessage ||
              getFriendlyErrorMessage(
                normalizedResult.status,
                "Unable to process response from server.",
              ),
          },
        });
      }
    } catch (err) {
      const status = err?.response?.status || null;
      const normalizedError = err?.response
        ? normalizeLambdaResult(status, err.response.data)
        : { ok: false, status, data: {} };
      const backendMessage =
        normalizedError?.data?.message ||
        normalizedError?.data?.error ||
        err?.message;

      setRequestResult({
        ok: false,
        status: normalizedError?.status || status,
        data: {
          ...(normalizedError?.data || {}),
          message:
            normalizedError?.data?.message ||
            normalizedError?.data?.error ||
            getFriendlyErrorMessage(
              normalizedError?.status || status,
              backendMessage,
            ),
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWallet = () => goToPreviousPage();
  const handleVerificationSubmit = () => {
    navigate("/kycuser");
  };
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/dashboard");
  };

  if (!walletID) {
    return (
      <Content className="appPageContent">
        <div className="appPageShell appPageShellNarrow">
          <div className="appSectionHeader appPageHeader">
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={handleBackToWallet}
              className="recipientDetailBackBtn"
            />
            <h2 className="appSectionTitle">Add Funds</h2>
          </div>
          <div className="appTableCard fundWalletCard">
            <Alert
              type="warning"
              showIcon
              message="Wallet information is missing"
              description="Open Add Funds from Dashboard so the wallet ID can be loaded."
            />
            <Button
              type="primary"
              style={{ marginTop: 16 }}
              onClick={handleBackToWallet}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </Content>
    );
  }

  const responseMessage =
    requestResult?.data?.message ||
    requestResult?.data?.error ||
    getFriendlyErrorMessage(
      requestResult?.status || null,
      "Unable to process response from server.",
    );

  const normalizedWatchedAmount = sanitizeAmountInput(watchedAmount).replace(
    /\.$/,
    "",
  );
  const pendingAmount = /^\d+(\.\d{1,2})?$/.test(normalizedWatchedAmount)
    ? Number(normalizedWatchedAmount)
    : 0;
  const projectedBalance =
    Number(availableBalance || 0) + (Number.isFinite(pendingAmount) ? pendingAmount : 0);
  return (
    <Content className="appPageContent">
      <div className="appPageShell appPageShellNarrow">
        <div className="appSectionHeader appPageHeader">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={handleBackToWallet}
            className="recipientDetailBackBtn"
          />
          <h2 className="appSectionTitle">Add Funds</h2>
        </div>

        <div className="appTableCard fundWalletCard">
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <div className="fundWalletTopMeta">
              <div className="fundWalletBalanceSummary">
                <span className="fundWalletBalanceLabel">
                  Available Balance
                </span>
                <strong>
                  ${formatCurrencyAmount(availableBalance)} {walletCurrency}
                </strong>
              </div>
              <div className="fundWalletBalanceArrow" aria-hidden="true">
                <span className="fundWalletBalanceArrowGlyph">→</span>
              </div>
              <div className="fundWalletProjectedSummary">
                <span className="fundWalletBalanceLabel">Balance After</span>
                <strong>
                  ${formatCurrencyAmount(projectedBalance)} {walletCurrency}
                </strong>
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              requiredMark={false}
              validateTrigger="onSubmit"
              className="fundWalletForm"
            >
              <Form.Item label="Amount">
                <div className="dashboardConverterInputRowV2 fundWalletAmountRow">
                  <span className="dashboardConverterCurrencyV2">
                    <img
                      src={walletFlag}
                      alt={walletCurrency}
                      className="dashboardCurrencyFlag"
                    />
                    <span className="dashboardConverterCurrencyText">
                      {walletCurrency}
                    </span>
                  </span>
                  <Form.Item
                    name="amount"
                    noStyle
                    rules={[
                      {
                        validator: (_, value) => {
                          const normalized = sanitizeAmountInput(value).replace(
                            /\.$/,
                            "",
                          );
                          if (!normalized) {
                            return Promise.reject(new Error("Enter an amount."));
                          }
                          if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
                            return Promise.reject(
                              new Error("Use numbers with up to 2 decimals."),
                            );
                          }
                          if (Number(normalized) <= 0) {
                            return Promise.reject(
                              new Error("Amount must be greater than 0."),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      controls={false}
                      min={0}
                      precision={2}
                      placeholder="0.00"
                      formatter={formatAmountInput}
                      parser={(value) => sanitizeAmountInput(value)}
                      onKeyDown={handleAmountKeyDown}
                      className="fundWalletAmountInput"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </div>
              </Form.Item>

              <Form.Item
                name="paymentMethod"
                label="Payment Method"
                rules={[
                  {
                    required: true,
                    message: "Select one payment method.",
                  },
                ]}
              >
                <Radio.Group
                  className="fundWalletRadioGroup"
                  buttonStyle="solid"
                >
                  {paymentMethodOptions.map((option) => (
                    <Radio.Button
                      key={option.value}
                      value={option.value}
                      className="fundWalletMethodOption"
                    >
                      <span className="fundWalletMethodInner">
                        <span className="fundWalletMethodIconWrap">
                          {option.icon}
                        </span>
                        <span className="fundWalletMethodLabel">
                          {option.label}
                        </span>
                      </span>
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>

              <Space className="fundWalletActions">
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={loading || isSubmitLocked}
                  className="signupbtn fundWalletSubmitBtn"
                >
                  {loading ? (
                    <Spin size="small" />
                  ) : isSubmitLocked ? (
                    "Submitted"
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Space>
            </Form>

            {requestResult && !requestResult.ok && (
              <Alert showIcon type="error" message={responseMessage} />
            )}
          </Space>
        </div>
      </div>

      <AppActionModal
        open={showVerificationModal}
        onCancel={() => setShowVerificationModal(false)}
        variant="warning"
        icon={<ExclamationCircleFilled />}
        title={verificationUi?.modalTitle || "Complete Verification"}
        description={
          verificationUi?.modalText ||
          "Please complete KYC/KYB to continue with add funds."
        }
        secondaryAction={{
          label: verificationUi?.modalSecondaryLabel || "Cancel",
          onClick: () => setShowVerificationModal(false),
        }}
        primaryAction={
          verificationUi?.showModalPrimary
            ? {
                label:
                  verificationUi?.modalPrimaryLabel || "Start Verification",
                onClick: handleVerificationSubmit,
              }
            : null
        }
      />

      <AppActionModal
        open={showSuccessModal}
        onCancel={handleSuccessModalClose}
        className="fundWalletResultModal"
        variant="success"
        icon={<CheckCircleFilled />}
        title="Request Submitted"
        description={
          successMessage ||
          "Your add fund request has been submitted successfully."
        }
        closable
        maskClosable={false}
        primaryAction={{
          label: "Back to Dashboard",
          onClick: handleSuccessModalClose,
          className: "overviewResultBtn fundWalletResultBtn",
        }}
      >
        <p className="appActionModalText fundWalletResultNote">
          Our CS team will contact you with payment instructions.
        </p>
      </AppActionModal>
    </Content>
  );
};

export default FundWallet;
